// 샘플 평가 접근 티켓 (설계 §4a).
//
// 실제 샘플 task entry의 `PUT /{id}/status?status=START` 응답이 서명 티켓을 발급하고,
// `GET /{id}/sets/{t}/{taskId}` 는 그 티켓을 되돌려받지 못하면 403 을 낸다. 샘플 평가는
// user_map_custom_assessment 행을 갖지 않아 서버가 "이 사람 지금 응시 중"을 DB로 판정할 수
// 없기 때문에, 게이트를 통과한 사실 자체를 서명해 클라이언트가 들고 다니는 구조다.
//
// sessionStorage 를 쓰지 않는다. 하드 새로고침으로 티켓이 사라져 시작 페이지 재진입 →
// START 발급 티켓이 사라져 다시 task entry 흐름을 타야 하는 것이 의도된 동작이다(설계 §4a).
//
// 모듈 레벨 상태인 이유는 좌석(useAssessmentSeat)과 같다 — 발급(응답 헤더 읽기)과 사용(요청
// 헤더 첨부)이 모두 API 레이어(package/apis/*)에서 일어나고, 거기엔 Nuxt setup 컨텍스트가
// 없어 useState() 를 쓸 수 없다.
//
// INVARIANT: 이 상태는 클라이언트에서만 쓴다. 모듈 레벨 Map 은 useState() 와 달리 요청 스코프가
// 아닌 Nitro 프로세스 싱글턴이라, SSR 시점 쓰기는 한 사용자의 티켓을 같은 워커의 동시 요청
// 전부에 누출시킨다. 좌석은 호출부가 onMounted/onBeforeUnmount 뿐이라 규약으로 지킬 수 있지만,
// 이 티켓은 쓰는 지점이 API 레이어 자체이고 API 호출은 SSR 에서도 실행될 수 있다. 그래서 규약
// 대신 setter 안의 가드로 막는다 — 호출부가 어디든 서버에서는 아무 일도 일어나지 않는다.

const TICKET_HEADER = 'X-Sample-Access';
const ISSUED_AT_HEADER = 'X-Sample-Access-Issued-At';

const tickets = new Map<number, { signature: string; issuedAt: string }>();

// axios 는 응답 헤더 키를 소문자로 정규화한다. 원본 대소문자도 함께 본다 — 못 읽으면 조용히
// 티켓 없는 상태가 되어 문제 조회가 403 이 되므로 실패가 늦게 드러난다.
const headerValue = (headers: Record<string, unknown>, name: string): string | null => {
    const raw = headers[name.toLowerCase()] ?? headers[name];
    return typeof raw === 'string' && raw.length > 0 ? raw : null;
};

/**
 * 샘플 START 응답이 발급한 티켓을 보관한다.
 *
 * 티켓이 없는 응답(자격 미충족은 200 + 헤더 없음, 비샘플은 애초에 발급 안 함)은 해당 평가의
 * 티켓만 지운다. 평가별로 보관하므로 다른 평가의 진행 중 티켓에는 영향을 주지 않는다.
 */
export const storeSampleAccessTicket = (assessmentId: number, headers: Record<string, unknown>): void => {
    if (typeof window === 'undefined') return; // 위 INVARIANT — SSR 시점 쓰기 금지

    const signature = headerValue(headers, TICKET_HEADER);
    const issuedAt = headerValue(headers, ISSUED_AT_HEADER);
    if (signature && issuedAt) {
        tickets.set(assessmentId, {signature, issuedAt});
    } else {
        tickets.delete(assessmentId);
    }
};

/** 문제 조회 GET 에 실을 헤더. 티켓이 없으면 비어 있어 엉뚱한 값이 나가지 않는다. */
export const sampleAccessHeader = (assessmentId: number): Record<string, string> => {
    const ticket = tickets.get(assessmentId);
    return ticket
        ? { [TICKET_HEADER]: ticket.signature, [ISSUED_AT_HEADER]: ticket.issuedAt }
        : {};
};
